import { forwardRef, useLayoutEffect, useRef } from "react";

import styled from "styled-components";
import Link from "next/link";
import { Form } from "react-bootstrap";
import {
  AttributesView,
  ColorAA,
  IconButton,
  JsonLog,
  TooltipProvider,
} from "@bagel-web/components";
import {
  ASHComponentVisualization,
  ASHComponentVisualizationProps,
} from "./ASHComponentCard";
import { CommonComponentState } from "../../types";

const StyledLink = styled(Link)`
  font-size: 0.875rem !important;
`;

export const SourceCell = styled.td`
  padding-right: 20px !important;
  white-space: nowrap;
`;

export const ValueCell = styled.td<{ $compactView?: boolean }>`
  white-space: ${({ $compactView }: { $compactView?: boolean }) =>
    $compactView ? "normal" : "pre-wrap"};
`;

export const MomentLinkCell = styled.td`
  white-space: nowrap;
  width: 1%;
  text-align: center;
`;

// Sticky over the scrolling rows, but under floating UI: Bootstrap's
// .sticky-top sits at 1020, over dropdown menus (1000).
export const StickyHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
`;

// Shared edit-mode affordances for the state visualizations' value cells.

// Revealed by hovering the cell (or keyboard focus); opacity keeps the
// buttons in the layout and tabbable.
export const RowActionButton = styled(IconButton)`
  opacity: 0;
  td:hover &,
  &:focus-visible {
    opacity: 1;
  }
`;

// The whole cell is the edit target; the pencil is the visual affordance,
// and its clicks just bubble here.
export const EditableValueCell = styled(ValueCell)`
  cursor: pointer;
`;

// Sized to the row text, so entering edit mode doesn't change the row
// height.
export const EditInput = styled(Form.Control)`
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding-top: 0;
  padding-bottom: 0;
`;

// The height is scripted (below), so the field never scrolls inside itself
// and offers no manual grip.
const GrowingTextArea = styled(EditInput)`
  overflow-y: hidden;
  resize: none;
`;

const fitToContent = (element: HTMLTextAreaElement) => {
  // Collapse first so deletions shrink it too.
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
};

// The multi-line variant of EditInput: a textarea that grows to fit its
// content, so the whole value stays visible while editing.
export const EditTextArea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    isInvalid?: boolean;
    "data-testid"?: string;
  }
>(function EditTextArea({ onChange, ...props }, ref) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  // Fit the opening value before paint: the editor mounts prefilled.
  useLayoutEffect(() => {
    if (innerRef.current) {
      fitToContent(innerRef.current);
    }
  }, []);
  return (
    <GrowingTextArea
      forwardedAs="textarea"
      rows={1}
      {...props}
      ref={(element: HTMLTextAreaElement | null) => {
        innerRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
        fitToContent(event.target);
        onChange?.(event);
      }}
    />
  );
});

const ClientWorkMarkText = styled.span`
  color: ${ColorAA.yellow};
  font-weight: bold;
  [data-bs-theme="light"] && {
    color: ${ColorAA.blue};
  }
`;

// Stands in for the moment link on entries that are the client's own work:
// an edit drops the origin provenance, so no origin means edited or added
// rather than seeded untouched. Only meaningful in edit mode.
export const ClientWorkMark = ({
  "data-testid": testId,
}: {
  "data-testid"?: string;
}) => <ClientWorkMarkText data-testid={testId}>*</ClientWorkMarkText>;

const StyledMark = styled.mark`
  background-color: ${ColorAA.yellow};
  color: ${ColorAA.lightBlack};
  padding: 0.1rem 0;
`;

export const HighlightedText = ({
  text,
  textFilter,
}: {
  text: string;
  textFilter?: string;
}) => {
  if (!textFilter) {
    return <>{text}</>;
  }
  const escaped = textFilter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === textFilter.toLowerCase() ? (
          <StyledMark key={i}>{part}</StyledMark>
        ) : (
          part
        )
      )}
    </>
  );
};

export const MomentLink = ({
  sessionId,
  agentId,
  item,
}: {
  sessionId: string;
  agentId: string;
  item: { session_id?: string | null; moment_id?: number | null };
}) => {
  let moment_link = null;
  if (item.moment_id && item.session_id) {
    moment_link = (
      <StyledLink
        href={`/sessions/${item.session_id}/agent/${agentId}?momentId=${item.moment_id}&selectedHistoryTab=state`}
      >
        {item.session_id !== sessionId && (
          <i className="me-2 bi-diagram-3-fill" />
        )}
        {item.moment_id}
      </StyledLink>
    );
    if (item.session_id !== sessionId) {
      moment_link = (
        <TooltipProvider
          tooltip={
            <AttributesView
              attributes={[
                {
                  name: "session_id",
                  label: "Parent Session ID",
                  value: item.session_id,
                },
              ]}
            />
          }
        >
          {moment_link}
        </TooltipProvider>
      );
    }
  }
  return moment_link;
};

const SimpleComponentVisualization: ASHComponentVisualization<
  CommonComponentState
> = ({
  componentState,
}: ASHComponentVisualizationProps<CommonComponentState>) => {
  return componentState ? (
    <JsonLog id={componentState.id} data={componentState.state} />
  ) : null;
};

export default SimpleComponentVisualization;
